import {useSigma} from "@react-sigma/core";
import React, {useMemo} from "react";

import {Badge, Card, ListGroup} from "react-bootstrap";
import {useSubNetworkContext} from "../../contexts/SubNetworkContext";
import {useTheCellMapContext} from "../../contexts/TheCellMapContext";
import {useAnnotationMap} from "../../hooks/useAnnotationMap";
import {UNANNOTATED_TERM_NAME} from "../../types/consts";
import {Annotation, NodeData} from "../../types/Types";
import {hexToStringRgba} from "../../utils/colour";
import LegendCircle from "../LegendCircle";

interface SubNetworkBioprocessLegendSectionProps {
  sortedTerms: Annotation[];
  annotationCounts: Record<string, number>;
  isHighlighted: (annotation: Annotation) => boolean;
  onEnter: (annotation: Annotation) => void;
  onLeave: () => void;
}

const SubNetworkBioprocessLegendSection = React.memo(({
  sortedTerms,
  annotationCounts,
  isHighlighted,
  onEnter,
  onLeave
}: SubNetworkBioprocessLegendSectionProps) => {
  return <>
    <Card.Title as="h5">Bioprocess-Enriched Network Regions</Card.Title>
    <ListGroup variant="flush">
      {sortedTerms.map(annotation => (
        <ListGroup.Item
          key={annotation.name}
          className="d-flex align-items-center text-white border-0 py-0"
          onMouseEnter={() => onEnter(annotation)}
          onMouseLeave={onLeave}
          style={{
            backgroundColor: isHighlighted(annotation) ? hexToStringRgba(annotation.colour, .35) : "transparent",
            boxShadow: isHighlighted(annotation) ? `inset 0 0 5px 2px ${annotation.colour}` : "none",
          }}
        >
          <div className="d-flex align-items-center">
            <LegendCircle colour={annotation.colour} size={".8em"} />
            <span className="ms-2">{annotation.name}</span>
          </div>
          <div className="flex-fill d-flex align-items-center justify-content-end ms-2">
            <Badge bg="dark" pill>{annotationCounts[annotation.name] || 0}</Badge>
          </div>
        </ListGroup.Item>
      ))}
    </ListGroup>
  </>
});

const SubNetworkLegend = React.memo(() => {
  const sigma = useSigma();
  const graph = sigma.getGraph();
  const annotationMap = useAnnotationMap();
  const {selectedGenes} = useTheCellMapContext();

  const {
    displayedTerms,
    highlightedTerm,
    setHighlightedTerm,
    setLegendHoveredTerm
  } = useSubNetworkContext();

  /** Tally the nodes annotated to each term. */
  const annotationCounts = useMemo(() => {
    if (selectedGenes.length === 0) return {} as Record<string, number>;

    const counts: Record<string, number> = {};

    graph.forEachNode((node) => {
      const attrs = graph.getNodeAttributes(node) as NodeData;
      const nodeAnnotation = annotationMap.get(attrs.e)?.name || UNANNOTATED_TERM_NAME;
      counts[nodeAnnotation] = (counts[nodeAnnotation] || 0) + 1;
    });

    return counts;
  }, [displayedTerms, graph, annotationMap, selectedGenes.length]);

  /** Sort terms by number of annotations, then alphabetically. */
  const sortedTerms = useMemo(() => {
    return [...displayedTerms].sort((a, b) => {
      if (a.name === UNANNOTATED_TERM_NAME) return 1;
      if (b.name === UNANNOTATED_TERM_NAME) return -1;

      const countA = annotationCounts[a.name] || 0;
      const countB = annotationCounts[b.name] || 0;

      /* If they have the same number of annotations, sort alphabetically. */
      if (countA === countB) {
        return a.name.localeCompare(b.name);
      }

      return countB - countA;
    });
  }, [displayedTerms, annotationCounts]);

  const isHighlighted = (term: Annotation) =>
    highlightedTerm === term;

  return <SubNetworkBioprocessLegendSection
    sortedTerms={sortedTerms}
    annotationCounts={annotationCounts}
    isHighlighted={isHighlighted}
    onEnter={(annotation) => {
      setHighlightedTerm(annotation);
      setLegendHoveredTerm(annotation);
    }}
    onLeave={() => {
      setHighlightedTerm(undefined);
      setLegendHoveredTerm(undefined);
    }}
  />
});

export default SubNetworkLegend;
